import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import '@/App.css';
import IntroSequence from '@/components/IntroSequence';
import ChatInterface from '@/components/ChatInterface';
import LoginPage from '@/components/LoginPage';
import PrivacyPage from '@/components/legal/PrivacyPage';
import TermsPage from '@/components/legal/TermsPage';
import { Toaster } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth token persistence (production reload fix).
// The backend also sets an httpOnly cookie, but in production the frontend
// (alivan.co.il) and backend live on different origins, so that cookie is
// third-party and gets dropped by Safari ITP / Firefox TCP / Chrome storage
// partitioning on page reload. Storing the token on the alivan.co.il origin
// and sending it as `Authorization: Bearer` survives reloads.
const AUTH_TOKEN_KEY = 'alivan-auth-token';
const getAuthToken = () => {
  try { return localStorage.getItem(AUTH_TOKEN_KEY); } catch (_) { return null; }
};
const setAuthToken = (t) => {
  try { if (t) localStorage.setItem(AUTH_TOKEN_KEY, t); } catch (_) { /* noop */ }
};
const clearAuthToken = () => {
  try { localStorage.removeItem(AUTH_TOKEN_KEY); } catch (_) { /* noop */ }
};
const authHeaders = () => {
  const t = getAuthToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// AuthCallback processes session_id from URL fragment after Google auth
const AuthCallback = ({ onAuthComplete }) => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        if (!sessionIdMatch) {
          navigate('/');
          return;
        }
        const sessionId = sessionIdMatch[1];

        const response = await fetch(`${API}/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });

        if (!response.ok) {
          console.error('Auth failed');
          navigate('/');
          return;
        }

        const userData = await response.json();
        // Persist token so it survives page reloads on alivan.co.il origin.
        if (userData && userData.session_token) {
          setAuthToken(userData.session_token);
        }
        // Also fetch full user (with intro_seen) via /auth/me
        const meResp = await fetch(`${API}/auth/me`, {
          credentials: 'include',
          headers: { ...authHeaders() },
        });
        const fullUser = meResp.ok ? await meResp.json() : userData;

        // Notify parent BEFORE clearing the hash so setUser runs first.
        onAuthComplete(fullUser);
        // Use react-router navigate (not window.history.replaceState) so
        // useLocation() actually updates and `isAuthCallback` flips to
        // false — otherwise AppContent keeps re-rendering AuthCallback
        // and any re-mount (StrictMode / iframe partitioning) would try
        // to redeem the already-consumed session_id, forcing a bounce
        // back to LoginPage. This is the persistent fix for the loop
        // reported in View Preview.
        navigate('/', { replace: true });
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/');
      }
    };

    processSession();
  }, [navigate, onAuthComplete]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#0A0A0A]">
      <div className="text-white font-outfit text-xl">
        <div className="animate-pulse">Iniciando sesión...</div>
      </div>
    </div>
  );
};

const AppContent = () => {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showIntro, setShowIntro] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  // Synchronous check for OAuth callback
  const isAuthCallback = location.hash?.includes('session_id=');

  useEffect(() => {
    if (isAuthCallback) {
      setAuthLoading(false);
      return;
    }
    checkAuth();
  }, [isAuthCallback]);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers: { ...authHeaders() },
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setShowIntro(!userData.intro_seen);
        const savedSessionId = localStorage.getItem(`alivan-session-${userData.user_id}`);
        if (savedSessionId) {
          setSessionId(savedSessionId);
        }
      } else if (response.status === 401) {
        // Token / cookie is genuinely invalid (server said so). Clear the
        // stored token so we don't keep sending a broken Bearer next time.
        clearAuthToken();
      }
      // For any other status (5xx, network partition, etc.) we deliberately
      // do NOT clear the token — a transient outage should not log the user
      // out. The user will retry.
    } catch (error) {
      console.error('Auth check failed:', error);
      // Do not clear token on network failure.
    } finally {
      setAuthLoading(false);
    }
  };

  const handleAuthComplete = (userData) => {
    setUser(userData);
    setShowIntro(!userData.intro_seen);
  };

  const handleSessionUpdate = (newSessionId) => {
    setSessionId(newSessionId);
    if (newSessionId && user) {
      localStorage.setItem(`alivan-session-${user.user_id}`, newSessionId);
    }
  };

  const handleIntroComplete = async () => {
    try {
      await fetch(`${API}/auth/mark-intro-seen`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...authHeaders() },
      });
    } catch (e) {
      console.error('Failed to mark intro seen', e);
    }
    setShowIntro(false);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...authHeaders() },
      });
    } catch (e) {
      console.error('Logout failed', e);
    }
    if (user) {
      localStorage.removeItem(`alivan-session-${user.user_id}`);
    }
    clearAuthToken();
    setUser(null);
    setSessionId(null);
    setShowIntro(false);
  };

  if (isAuthCallback) {
    return <AuthCallback onAuthComplete={handleAuthComplete} />;
  }

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0A0A0A]">
        <div className="text-white font-outfit text-xl animate-pulse">Cargando...</div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  if (showIntro) {
    return (
      <IntroSequence
        onComplete={handleIntroComplete}
        onSkip={handleIntroComplete}
      />
    );
  }

  return (
    <ChatInterface
      user={user}
      sessionId={sessionId}
      setSessionId={handleSessionUpdate}
      onLogout={handleLogout}
    />
  );
};

function App() {
  return (
    <div className="App" data-testid="app-container">
      <BrowserRouter>
        <Routes>
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="*" element={<AppContent />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
